import { roundToCents } from "@common/helpers/monetary";
import { labeledAdjustmentToStored } from "./nativeInvoiceAdjustment";
import {
    DEFAULT_NATIVE_INVOICE_DEPOSIT_SIGN,
    DEFAULT_NATIVE_INVOICE_DISCOUNT_SIGN,
    DEFAULT_NATIVE_INVOICE_TAX_SIGN,
    NativeInvoiceAdjustmentMode,
    NativeInvoiceAdjustmentSign,
    NativeInvoiceTotals,
} from "./nativeInvoiceFormTypes";

export function multiplyNativeInvoiceLineAmount({
    quantity,
    unitAmount,
}: {
    quantity: number | null;
    unitAmount: number | null;
}): number {
    return (quantity ?? 0) * (unitAmount ?? 0);
}

export function computeNativeInvoiceLineAmount({
    quantity,
    unitAmount,
}: {
    quantity: number | null;
    unitAmount: number | null;
}): number {
    return roundToCents(
        multiplyNativeInvoiceLineAmount({ quantity, unitAmount }),
    );
}

export function hasNonZeroNativeInvoiceLine(
    lines: Array<{ quantity: number | null; unitAmount: number | null }>,
): boolean {
    return lines.some((line) => computeNativeInvoiceLineAmount(line) !== 0);
}

// Resolve a Tax/Discount/Deposit row to its effective monetary amount: percent
// rows take the matching share of the subtotal, amount rows use the value as-is.
// The labeled input may be negative (a flip of that row's default direction).
function computeAdjustmentAmount({
    value,
    mode,
    subtotal,
}: {
    value: number;
    mode: NativeInvoiceAdjustmentMode;
    subtotal: number;
}): number {
    return mode === "percent" ? (subtotal * value) / 100 : value;
}

function resolveLabeledAdjustment({
    labeledValue,
    mode,
    subtotal,
    defaultSign,
}: {
    labeledValue: number;
    mode: NativeInvoiceAdjustmentMode;
    subtotal: number;
    defaultSign: NativeInvoiceAdjustmentSign;
}): { amount: number; sign: NativeInvoiceAdjustmentSign } {
    return {
        amount: roundToCents(
            computeAdjustmentAmount({
                value: labeledValue,
                mode,
                subtotal,
            }),
        ),
        sign: labeledAdjustmentToStored({ labeledValue, defaultSign }).sign,
    };
}

// Single source of truth for the invoice money math, extracted as a pure
// function so the totals can be unit-tested in isolation (see
// computeNativeInvoiceTotals.test.ts).
//
// Invariants exercised by the tests:
//  1. subtotal = Σ roundToCents(line.quantity × line.unitAmount), so it always
//     matches the sum of the per-line amounts the snapshot/PDF prints.
//  2. Each adjustment is a labeled signed amount: percent rows take that share
//     of the subtotal, amount rows use the entered value as-is. A minus flips
//     the field default (Tax adds, Discount/Deposit subtract).
//  3. taxAmount / discountAmount / depositAmount are kind-native signed values
//     (persist + split/JE). taxSign / discountSign / depositSign are derived
//     for PDF display only.
//  4. total = subtotal + tax − discount − deposit (classic invoice formula).
//  5. Header percent adjustments are always relative to the subtotal (never to
//     a running total), so row order does not change the result. Per-line
//     percent adjustments are relative to that line's amount.
export interface NativeInvoiceTotalsLine {
    quantity: number | null;
    unitAmount: number | null;
    tax?: number;
    discount?: number;
    deposit?: number;
}

export function computeNativeInvoiceLineAdjustmentAmount({
    labeledValue,
    mode,
    baseAmount,
}: {
    labeledValue: number;
    mode: NativeInvoiceAdjustmentMode;
    baseAmount: number;
}): number {
    return roundToCents(
        computeAdjustmentAmount({
            value: labeledValue,
            mode,
            subtotal: baseAmount,
        }),
    );
}

export function computeNativeInvoiceWeightedAveragePercent({
    lines,
    field,
}: {
    lines: NativeInvoiceTotalsLine[];
    field: "tax" | "discount" | "deposit";
}): number {
    let weightedSum = 0;
    let totalAmount = 0;
    for (const line of lines) {
        const lineAmount = computeNativeInvoiceLineAmount(line);
        weightedSum += lineAmount * (line[field] ?? 0);
        totalAmount += lineAmount;
    }
    if (totalAmount === 0) {
        return 0;
    }
    return roundToCents(weightedSum / totalAmount);
}

function sumPerLineAdjustment({
    lines,
    mode,
    field,
    defaultSign,
}: {
    lines: NativeInvoiceTotalsLine[];
    mode: NativeInvoiceAdjustmentMode;
    field: "tax" | "discount" | "deposit";
    defaultSign: NativeInvoiceAdjustmentSign;
}): { amount: number; sign: NativeInvoiceAdjustmentSign } {
    const amount = roundToCents(
        lines.reduce((sum, line) => {
            const lineAmount = computeNativeInvoiceLineAmount(line);
            return (
                sum +
                computeNativeInvoiceLineAdjustmentAmount({
                    labeledValue: line[field] ?? 0,
                    mode,
                    baseAmount: lineAmount,
                })
            );
        }, 0),
    );
    return {
        amount,
        sign: labeledAdjustmentToStored({ labeledValue: amount, defaultSign })
            .sign,
    };
}

export function computeNativeInvoiceTotals({
    lines,
    tax,
    taxMode,
    discount,
    discountMode,
    deposit,
    depositMode,
    isTaxPerLineItem = false,
    isDiscountPerLineItem = false,
    isDepositPerLineItem = false,
}: {
    lines: NativeInvoiceTotalsLine[];
    tax: number;
    taxMode: NativeInvoiceAdjustmentMode;
    discount: number;
    discountMode: NativeInvoiceAdjustmentMode;
    deposit: number;
    depositMode: NativeInvoiceAdjustmentMode;
    isTaxPerLineItem?: boolean;
    isDiscountPerLineItem?: boolean;
    isDepositPerLineItem?: boolean;
}): NativeInvoiceTotals {
    const subtotal = roundToCents(
        lines.reduce(
            (sum, line) => sum + computeNativeInvoiceLineAmount(line),
            0,
        ),
    );

    const taxAdjustment = isTaxPerLineItem
        ? sumPerLineAdjustment({
              lines,
              mode: taxMode,
              field: "tax",
              defaultSign: DEFAULT_NATIVE_INVOICE_TAX_SIGN,
          })
        : resolveLabeledAdjustment({
              labeledValue: tax,
              mode: taxMode,
              subtotal,
              defaultSign: DEFAULT_NATIVE_INVOICE_TAX_SIGN,
          });
    const discountAdjustment = isDiscountPerLineItem
        ? sumPerLineAdjustment({
              lines,
              mode: discountMode,
              field: "discount",
              defaultSign: DEFAULT_NATIVE_INVOICE_DISCOUNT_SIGN,
          })
        : resolveLabeledAdjustment({
              labeledValue: discount,
              mode: discountMode,
              subtotal,
              defaultSign: DEFAULT_NATIVE_INVOICE_DISCOUNT_SIGN,
          });
    const depositAdjustment = isDepositPerLineItem
        ? sumPerLineAdjustment({
              lines,
              mode: depositMode,
              field: "deposit",
              defaultSign: DEFAULT_NATIVE_INVOICE_DEPOSIT_SIGN,
          })
        : resolveLabeledAdjustment({
              labeledValue: deposit,
              mode: depositMode,
              subtotal,
              defaultSign: DEFAULT_NATIVE_INVOICE_DEPOSIT_SIGN,
          });

    const total = roundToCents(
        subtotal +
            taxAdjustment.amount -
            discountAdjustment.amount -
            depositAdjustment.amount,
    );

    return {
        subtotal,
        taxAmount: taxAdjustment.amount,
        discountAmount: discountAdjustment.amount,
        depositAmount: depositAdjustment.amount,
        taxSign: taxAdjustment.sign,
        discountSign: discountAdjustment.sign,
        depositSign: depositAdjustment.sign,
        total,
    };
}
